/* import __COLOCATED_TEMPLATE__ from './fin-identity.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type PulseAccordion } from 'glint/pulse';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import { isEmpty, isPresent } from '@ember/utils';
interface Args {
  accordion: PulseAccordion;
  onContinue: () => void;
}

export default class FinIdentity extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @tracked newAgentId?: string;

  @action openSectionChanged() {
    this.newAgentId = undefined;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData() {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData &&
      !this.adminOptions.length
    );
  }

  get isDisabled() {
    return (
      this.salesforceConfig?.isPendingSetup ||
      !this.salesforceSetupData.hasPermissionSetAssignedToInstaller ||
      false
    );
  }

  get isComplete() {
    return (
      !this.isDisabled &&
      isPresent(this.selectedAgentName) &&
      this.selectedAgent?.is_service_cloud_user
    );
  }

  get adminOptions() {
    let admins = this.salesforceSetupData.admins.map((admin) => ({
      text: admin.name,
      value: admin.id,
      isDisabled: !admin.is_service_cloud_user,
      tooltipText:
        !admin.is_service_cloud_user &&
        this.intl.t(
          'standalone.deploy.salesforce.cases.connect.fin-identity-section.update-selected-user',
        ),
    }));

    if (isEmpty(admins)) {
      return [
        {
          text: this.intl.t(
            'standalone.deploy.salesforce.cases.connect.fin-identity-section.select-agent.no-users-available',
          ),
          value: '',
          isDisabled: true,
        },
      ];
    }

    return admins;
  }

  get selectedAgent() {
    if (this.isLoading) {
      return undefined;
    }

    return this.salesforceSetupData.admins.find((admin) => admin.id === this.selectedAgentId);
  }

  get selectedAgentName(): string | undefined {
    return this.selectedAgent?.name;
  }

  get hasError(): boolean {
    if (this.isLoading) {
      return false;
    }

    return (
      (isEmpty(this.selectedAgentId) && isEmpty(this.newAgentId)) ||
      !this.selectedAgent?.is_service_cloud_user
    );
  }

  get selectedAgentId(): string | undefined {
    if (this.salesforceConfig.finSalesforceUserId) {
      return this.salesforceConfig.finSalesforceUserId;
    }
    return undefined;
  }

  get isAgentUpdated() {
    return this.newAgentId && this.salesforceConfig.finSalesforceUserId !== this.newAgentId;
  }

  get manageUsersUrl() {
    return this.salesforceConfig?.buildLightningSetupUrl('ManageUsers/home');
  }

  @action setAgentId(agentId: string) {
    this.newAgentId = agentId;
  }

  @action continue() {
    if (this.args.onContinue) {
      this.args.onContinue();
    }
  }

  @action async continueAndMaybeUpdateIdentity() {
    try {
      if (this.isAgentUpdated) {
        await this.salesforceConfig.setFinUser(this.newAgentId!);
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'standalone.deploy.salesforce.cases.connect.fin-identity-section.success-notification',
          ),
        );
      }
      this.continue();
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.fin-identity-section.error-notification',
        ),
      });
      console.error(e);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Connect::FinIdentity': typeof FinIdentity;
    'standalone/deploy/salesforce/cases/connect/fin-identity': typeof FinIdentity;
  }
}
